/**
 * The eval-dialog edit-flow hook (spec 5.3): binds the SHARED edit layer
 * ({@link useEditLayer}) to its browser-local working-log atom and adds the eval
 * surface's seed step — seed the working copy from the committed ops-log and track the
 * dirty flag against the committed baseline. The dialog no longer commits itself; the
 * page's single Evaluate carries the captured overrides (INS-10671). The gesture
 * transforms (drag / stacked pick / bulk shift / revert) live in the shared layer, so
 * they cannot drift from the DPS preview.
 */

import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEditLayer } from './use-edit-layer';
import {
  baselineEntriesAtom,
  hasUncommittedEditsAtom,
  workingEntriesAtom,
} from './atoms';
import type {
  BulkShiftResult,
  DateOverrideWrite,
  MergedDate,
  MilestoneKey,
  RevertScope,
  ScheduleOverrideEntry,
  ScheduleRow,
  SlotId,
} from './types';

export interface ScheduleEditsApi {
  workingEntries: ScheduleOverrideEntry[];
  hasUncommittedEdits: boolean;
  editedDateCount: number;
  /**
   * Seeds the dialog on open. `baseline` is the committed (last-calc) set — the dirty
   * flag compares against it; `working` (default: `baseline`) is what the dialog shows,
   * so a reopen can restore prior UNCOMMITTED edits (from the working config) while still
   * flagging them as uncommitted against the last calc.
   */
  seed: (
    baseline: ScheduleOverrideEntry[],
    working?: ScheduleOverrideEntry[]
  ) => void;
  /** Appends a single date override (drag / stacked pick / table cell edit). */
  applyDrag: (slotId: SlotId, field: MilestoneKey, isoDay: string) => void;
  /**
   * Multi-marker drag: commits the grabbed marker's new date AND shifts every other
   * selected point by the same delta, in one working-copy transform.
   */
  applyMultiDrag: (
    rows: ScheduleRow[],
    primary: { slotId: SlotId; field: MilestoneKey; iso: string },
    others: Array<{ slotId: SlotId; field: MilestoneKey }>,
    deltaMs: number
  ) => void;
  /** Merges a validated batch of CSV writes (value = set, null = revert) in one commit. */
  applyCsvWrites: (writes: DateOverrideWrite[]) => void;
  /** Relative-month bulk shift over selected points; returns the applied/skip summary. */
  bulkShift: (
    rows: ScheduleRow[],
    points: Array<{ slotId: SlotId; field: MilestoneKey }>,
    offsetMonths: number
  ) => BulkShiftResult;
  /** Reverts at milestone / slot / selection / whole-schedule granularity. */
  revertScope: (scope: RevertScope) => void;
  /** The row's effective dates (base overlaid with the working overrides). */
  effectiveDates: (row: ScheduleRow) => Record<MilestoneKey, MergedDate>;
}

/**
 * The schedule edit API. Delegates every gesture to the shared {@link useEditLayer}
 * bound to the eval dialog's working-log atom and layers on the Calculate commit.
 *
 * @return {ScheduleEditsApi} The edit API.
 */
export function useScheduleEdits(): ScheduleEditsApi {
  const layer = useEditLayer(workingEntriesAtom);
  const setBaseline = useSetAtom(baselineEntriesAtom);
  const hasEdits = useAtomValue(hasUncommittedEditsAtom);

  const seed = useCallback(
    (
      baseline: ScheduleOverrideEntry[],
      working: ScheduleOverrideEntry[] = baseline
    ) => {
      layer.setEntries(working);
      setBaseline(baseline);
    },
    [layer, setBaseline]
  );

  return {
    workingEntries: layer.workingEntries,
    hasUncommittedEdits: hasEdits,
    editedDateCount: layer.editedDateCount,
    seed,
    applyDrag: layer.applyDrag,
    applyMultiDrag: layer.applyMultiDrag,
    applyCsvWrites: layer.applyCsvWrites,
    bulkShift: layer.bulkShift,
    revertScope: layer.revertScope,
    effectiveDates: layer.effectiveDates,
  };
}
